import React, { useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import NavbarScroll from "../../components/subComponents/NavbarScroll";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import { useItemCategoryInfinite } from "../../hooks/useItemCategoryInfinite";
import PremiumBusinessCard from "./BusinessCard";
import { saffronRout, saffronURL } from "../../Url";

const keepKeys = [
  "country",
  "state",
  "city",
  "language",
  "category",
  "category_name",
  "featured",
];

const pickFilters = (search) => {
  const current = new URLSearchParams(search);
  const next = new URLSearchParams();

  keepKeys.forEach((k) => {
    const v = current.get(k);
    if (v) next.set(k, v);
  });

  return next;
};

const safeArray = (v) => (Array.isArray(v) ? v : []);
const addPosition = (arr) =>
  safeArray(arr).map((item, i) => ({ ...item, position: i }));

const Business = () => {
  const {
    selectedCountry,
    selectedCategory,
    selectedLanguage,
    selectedState,
    selectedCity,
  } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const categoryName = searchParams.get("category_name") || "";
  const isFeatured = searchParams.get("featured") === "1";
    const sectionKey = searchParams.get("section"); // ✅ persistent


  // ✅ Infinite list (Features jaisa)
  const {
    item: listings,
    ref,
    hasNextPage,
    isFetchingNextPage,
    isPending: listPending,
  } = useItemCategoryInfinite({
    key: "connect_business",
    endpoint: "listing",      // ✅ match your list endpoint
    listKey: "listings",      // ✅ API returns data.listings
    category_name: categoryName,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    selectedState,
    selectedCity,
    enabled: true,
    threshold: 0.7,
    featured: sectionKey === "features" ? 1 : undefined,
  });

   const { data: featureListData = [] } = useQuery({
    queryKey: [
      "featureListData",
      selectedCountry,
      selectedCategory,
      selectedLanguage,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);
      params.append("is_featured", 1);
      const res = await axios.get(
        `${saffronURL}/api/v1/public/get-feature-listing?${params.toString()}`,
      );
      return res.data;
    },
    retry: false,
    staleTime: 1000 * 60 * 60,
  });



  const hasListings = (listings?.length || 0) > 0;

  // ✅ Menu
  const { data: menuData } = useQuery({
    queryKey: [
      "menuData_connect_slide",
      selectedCountry,
      selectedLanguage,
      selectedCategory,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);

      const url = `${saffronURL}/api/v1/public/menu?${params.toString()}`;
      const resp = await axios.get(url);
      return resp.data;
    },
    enabled: !!(selectedCountry || selectedLanguage || selectedCategory),
    staleTime: 1000 * 60 * 10,
    retry: 2,
  });

  const navbaritems = useMemo(
    () => menuData?.data?.items?.find((item) => item.key === "business"),
    [menuData]
  );

  // ✅ Add position for prev/next in details


    const listWithPos = useMemo(
      () => addPosition(sectionKey === "features"   ? featureListData?.data : listings),
      [featureListData, listings, sectionKey],
    );
  

  const handleCategorySelect = (child) => {
    const params = new URLSearchParams(searchParams);

    if (child?.code) params.set("category_name", child.code);
    else params.delete("category_name");

    if (isFeatured) params.set("featured", "1");

    setSearchParams(params, { replace: false });
    window.scrollTo(0, 0);
  };

  const handleOpenDetails = (item) => {
    const slug = item?.slug || item?.id;
    if (!slug) return;

    const next = pickFilters(location.search);

    // ✅ IMPORTANT: sectionKey should match details page map key
    // If details page map uses "listings" → keep it "listings"
    next.set("section", "listingsList");

    navigate(
      {
        pathname: `${saffronRout}/details/listing/${slug}`,
        search: `?${next.toString()}`,
      },
      {
        state: {
          sectionKey: "listingsList",
          position: item.position ?? 0,
        },
      }
    );

    window.scrollTo(0, 0);
  };

  return (
    <>
      <NavbarScroll
        items={navbaritems}
        activeCode={categoryName}
        onSelect={handleCategorySelect}
      />

      <section className="py-3 px-1 md:px-5 lg:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
          {listWithPos.map((item) => (
            <PremiumBusinessCard
              key={item.id || item.slug}
              data={item}
              loading={false}
              onArticleClick={() => handleOpenDetails(item)}
            />
          ))}
        </div>

        {/* Status */}
        {listPending && <p className="text-gray-500 mt-4">Loading listings…</p>}

        {!listPending && !hasListings && (
          <p className="text-gray-400 italic mt-4">
            No listings found in <span className="font-medium">'Business'</span>
          </p>
        )}

        {/* ✅ Infinite scroll trigger */}
        {hasNextPage && <div ref={ref} className="h-10" />}

        {isFetchingNextPage && (
          <div className="text-center text-sm text-gray-500 py-4">
            Loading more...
          </div>
        )}
      </section>
    </>
  );
};

export default Business;